import {
  BaseCard,
  Primary,
  SvgIcon,
  Text,
  TextLink,
} from '@cinch-labs/design-system'
import styles from './choose-store-card.module.css'
import { MouseEventHandler, useEffect, useState } from 'react'
import {
  useAnalytics,
  EventNames,
  TrackingEventTypes,
} from '@cinch-nx/shared-analytics'
import cx from 'classnames'
import { Coordinates, OpeningHours } from '../../hooks/types'
import useIsMobile from '../../hooks/use-is-mobile'
import { OpeningHoursDrawer } from '../opening-hours-drawer'
import { formatOpeningHours } from '../../utils/format-opening-hours'

type StoreCardProps = {
  index: number
  distance: number
  name: string
  address: string
  coordinates: Coordinates
  onSelect: () => void
  isSelected: boolean
  onMapClick: () => void
  showMapLink: boolean
  customerPostcode?: string
  ariaLabel?: string
  openingHours?: OpeningHours
  displayName: string
  under60Months: boolean
  distanceFromOEM?: number
}

export const ChooseStoreCard: React.FC<StoreCardProps> = ({
  index,
  distance,
  name,
  address,
  coordinates,
  onSelect,
  isSelected,
  onMapClick,
  showMapLink,
  customerPostcode,
  ariaLabel,
  openingHours,
  displayName,
  under60Months,
  distanceFromOEM,
}) => {
  const { trackEvent } = useAnalytics()
  const { isMobile } = useIsMobile()
  const [showOpeningHours, setShowOpeningHours] = useState(false)

  useEffect(() => {
    if (index === 0 && distance > 0) {
      const oemOption = under60Months && distanceFromOEM && distanceFromOEM < 21
      trackEvent({
        eventName: `Load stores`,
        type: TrackingEventTypes.ADOBE,
        data: {
          event: {
            name: EventNames.load,
            category: 'cinch_servicing',
            action: oemOption
              ? 'store_list_displayed_OEM_options'
              : 'store_list_displayed',
            label: oemOption ? 'make_a_booking_OEM_options' : 'make_a_booking',
            type: name,
            value: distance,
          },
        },
      })
    }
  }, [index])

  const onCardClick: MouseEventHandler<HTMLDivElement> = (event) => {
    const target = event.target as HTMLElement

    if (!target.closest('button, a')) {
      onSelect()
    }
  }

  const handleOpenOpeningHours = (e: React.MouseEvent) => {
    e.stopPropagation()
    setShowOpeningHours((isOpen) => (isMobile ? true : !isOpen))

    trackEvent({
      eventName: `Toggle Store Info`,
      type: TrackingEventTypes.ADOBE,
      data: {
        event: {
          name: EventNames.click,
          category: 'cinch_servicing',
          action: 'opening_hours',
          label: name,
          type: 'list_view',
        },
      },
    })
  }

  return (
    <>
      <BaseCard
        className={cx(
          styles.card,
          index === 0 ? '' : styles.marginTop,
          isSelected ? styles.selected : '',
        )}
      >
        <div onClick={onCardClick}>
          <Text
            element="h3"
            fontSizeFixed="m"
            fontWeight="bold"
            className={styles.name}
          >
            {displayName}
          </Text>

          <Text fontSizeFixed="s" className={styles.address}>
            {address}
          </Text>

          {distance !== 0 && (
            <Text fontSizeFixed="s" className={styles.distance}>
              {distance} miles away
            </Text>
          )}

          {openingHours && (
            <div className={styles.openingHoursLink}>
              <TextLink
                onClick={handleOpenOpeningHours}
                data-testid="opening-hours-link"
                id="opening-hours-link"
                aria-expanded={isMobile ? undefined : showOpeningHours}
                aria-controls={isMobile ? undefined : `opening-hours-${index}`}
              >
                <Text fontSizeFixed="s" className={styles.openingHoursLinkText}>
                  Opening hours{' '}
                  <SvgIcon
                    name={isMobile ? 'chevron-right' : 'chevron-down'}
                    size={12}
                    className={cx(
                      styles.openingHoursChevron,
                      showOpeningHours && !isMobile
                        ? styles.openingHoursChevronExpanded
                        : '',
                    )}
                  />
                </Text>
              </TextLink>
            </div>
          )}

          {!isMobile && showOpeningHours && openingHours && (
            <div
              id={`opening-hours-${index}`}
              className={styles.desktopOpeningHours}
              data-testid="desktop-opening-hours"
              onClick={(event) => event.stopPropagation()}
            >
              {formatOpeningHours(openingHours).map(({ day, hours }) => (
                <div key={day} className={styles.openingHoursRow}>
                  <Text fontSizeFixed="s">{day}</Text>
                  <Text fontSizeFixed="s" fontWeight="semibold">
                    {hours}
                  </Text>
                </div>
              ))}
            </div>
          )}

          {showMapLink && customerPostcode && !isMobile && (
            <div className={styles.mapLink}>
              <TextLink
                onClick={onMapClick}
                element={'a'}
                href={`https://www.google.com/maps/dir/?api=1&origin=${customerPostcode}&destination=${coordinates.latitude},${coordinates.longitude}`}
                target={'_blank'}
                data-testid={'service-centre-map-link'}
                id={'service-centre-map-link'}
              >
                <Text fontSizeFixed="s">
                  <SvgIcon name="new-tab" /> Map and directions
                </Text>
              </TextLink>
            </div>
          )}

          <Primary
            onClick={onSelect}
            size="large"
            classNames={{ button: styles.button }}
            aria-label={ariaLabel || `Select ${name}`}
            data-testid={'choose-service-centre-button'}
            id={'choose-service-centre-button'}
          >
            <div
              data-testid={'choose-service-centre-button-text'}
              id={'choose-service-centre-button-text'}
            >
              Select
            </div>
          </Primary>
        </div>
      </BaseCard>

      {isMobile && showOpeningHours && openingHours && (
        <OpeningHoursDrawer
          storeName={name}
          openingHours={openingHours}
          onClose={() => setShowOpeningHours(false)}
        />
      )}
    </>
  )
}
